import { useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/dashboard/Panel';
import { UpdatedLabel } from '@/components/dashboard/UpdatedLabel';
import { AttentionStrip } from '@/components/dashboard/AttentionStrip';
import { StatsRow } from '@/components/dashboard/StatsRow';
import { EndpointCapacityPanel } from '@/components/dashboard/EndpointCapacityPanel';
import { RecentDeploymentsPanel } from '@/components/dashboard/RecentDeploymentsPanel';
import { RecentCatalogsPanel } from '@/components/dashboard/RecentCatalogsPanel';
import { ActivityPanel } from '@/components/dashboard/ActivityPanel';
import { InfrastructurePanel } from '@/components/dashboard/InfrastructurePanel';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { useResourceData } from '@/lib/hooks/useResourceData';
import { computeAttention } from '@/lib/attention';
import { computeCapacity, deployedCountByCatalog, endpointCapacity } from '@/lib/dashboardCompute';
import type { Container } from '@/types/container';
import type { Volume } from '@/types/volume';
import type { Network } from '@/types/network';
import type { Stack, Service } from '@/types/stack';
import { RUNNING_STATUSES } from '@/lib/appStatus';

const POLL_MS = 30_000;
const CATALOG_POLL_MS = 120_000;
const DAY_MS = 24 * 60 * 60 * 1000;

// Module-level fetchers keep identity stable across renders.
const fetchEndpoints = () => api.endpoints.listWithResourcesAll();
const fetchApps = () => api.apps.listAll();
const fetchDeployments = () => api.deployments.list();
const fetchInstances = () => api.instances.list();
const fetchAuditLogs = () => api.auditLogs.list({ limit: 8 }).then((r) => r.items);
// Non-fatal: attention treats a missing TLS state as "nothing to report".
// Certificates are per domain, so each domain's detail is read too (few domains).
const fetchTls = async () => {
  const [settings, domains] = await Promise.all([
    api.settings.tls.get().catch(() => null),
    // allSettled: one unreadable domain must not hide the others' warnings.
    api.domains
      .list()
      .then((list) => Promise.allSettled(list.map((d) => api.domains.get(d.id))))
      .then((results) => results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : [])))
      .catch(() => null),
  ]);
  return { settings, domains };
};
const fetchCatalogs = () => api.catalog.list();

export function DashboardPage() {
  const endpointsP = usePolledData(fetchEndpoints, POLL_MS);
  const appsP = usePolledData(fetchApps, POLL_MS);
  const deploymentsP = usePolledData(fetchDeployments, POLL_MS);
  const instancesP = usePolledData(fetchInstances, POLL_MS);
  const logsP = usePolledData(fetchAuditLogs, POLL_MS);
  const tlsP = usePolledData(fetchTls, POLL_MS);
  const catalogsP = usePolledData(fetchCatalogs, CATALOG_POLL_MS);

  // Infrastructure counts: fetched once per load / manual refresh, not polled.
  const containers = useResourceData<Container>('containers');
  const volumes = useResourceData<Volume>('volumes');
  const networks = useResourceData<Network>('networks');
  const stacks = useResourceData<Stack>('stacks');
  const services = useResourceData<Service>('services');

  const endpoints = endpointsP.data;
  const apps = appsP.data;
  const deployments = deploymentsP.data;
  const instances = instancesP.data;
  const tls = tlsP.data?.settings ?? null;
  const domains = tlsP.data?.domains ?? null;

  const latest = useMemo(() => {
    const times = [endpointsP, appsP, deploymentsP, instancesP, logsP, tlsP, catalogsP]
      .map((p) => p.updatedAt)
      .filter((t): t is number => t !== null);
    return times.length ? Math.max(...times) : null;
  }, [endpointsP, appsP, deploymentsP, instancesP, logsP, tlsP, catalogsP]);

  const refetchEndpoints = endpointsP.refetch;
  const refetchApps = appsP.refetch;
  const refetchDeployments = deploymentsP.refetch;
  const refetchInstances = instancesP.refetch;
  const refetchLogs = logsP.refetch;
  const refetchTls = tlsP.refetch;
  const refetchCatalogs = catalogsP.refetch;
  const refetchContainers = containers.refetch;
  const refetchVolumes = volumes.refetch;
  const refetchNetworks = networks.refetch;
  const refetchStacks = stacks.refetch;
  const refetchServices = services.refetch;
  const refetchInfra = useCallback(() => {
    refetchContainers();
    refetchVolumes();
    refetchNetworks();
    refetchStacks();
    refetchServices();
  }, [refetchContainers, refetchVolumes, refetchNetworks, refetchStacks, refetchServices]);
  const refetchAll = useCallback(() => {
    refetchEndpoints();
    refetchApps();
    refetchDeployments();
    refetchInstances();
    refetchLogs();
    refetchTls();
    refetchCatalogs();
    refetchInfra();
  }, [
    refetchEndpoints,
    refetchApps,
    refetchDeployments,
    refetchInstances,
    refetchLogs,
    refetchTls,
    refetchCatalogs,
    refetchInfra,
  ]);

  // The four critical sources: attention is only meaningful once each has settled.
  const settled = [endpointsP, appsP, deploymentsP, instancesP].every((p) => p.data !== undefined || p.error !== null);
  const incomplete = [endpointsP, appsP, deploymentsP, instancesP].some((p) => p.error !== null);

  // `now` is the time of the latest successful fetch: avoids Date.now() during render
  // and keeps the 24h windows stable between polls.
  const now = latest ?? 0;

  const attention = useMemo(
    () =>
      settled
        ? computeAttention({
            endpoints: endpoints ?? [],
            apps: apps ?? [],
            deployments: deployments ?? [],
            instances: instances ?? [],
            tls,
            domains,
            now,
          })
        : undefined,
    [settled, endpoints, apps, deployments, instances, tls, domains, now],
  );

  const deployments24h = useMemo(() => {
    if (!deployments) return undefined;
    return deployments.filter((d) => {
      const t = Date.parse(d.created_at);
      return Number.isFinite(t) && now - t <= DAY_MS;
    }).length;
  }, [deployments, now]);

  const capacityPercent = useMemo(() => {
    if (!endpoints || !apps) return undefined;
    const c = computeCapacity(endpoints, apps);
    return Math.max(c.cpu.percent, c.memory.percent, c.storage.percent);
  }, [endpoints, apps]);

  const capacityItems = useMemo(
    () => (endpoints && apps ? endpointCapacity(endpoints, apps) : []),
    [endpoints, apps],
  );
  const endpointNames = useMemo(() => {
    const names: Record<number, string> = {};
    for (const e of endpoints ?? []) names[e.id] = e.name;
    return names;
  }, [endpoints]);
  const deployedCounts = useMemo(() => deployedCountByCatalog(apps ?? []), [apps]);

  // Infra: pagination means `data` may hold only the first page; `total` is exact.
  const infra = [containers, volumes, networks, stacks, services];
  const infraLoading = infra.some((h) => h.loading);
  const infraResolved = infra.some((h) => !h.loading && h.error === null);
  const infraAllFailed = infra.every((h) => !h.loading && h.error !== null);
  const infraError = infraAllFailed ? (containers.error ?? 'Failed to load') : null;
  const infraPartialError = infraResolved && infra.some((h) => h.error !== null);
  const containerStats = useMemo(() => {
    if (containers.loading || containers.error !== null) return undefined;
    const complete = containers.data.length >= containers.total;
    return {
      total: containers.total,
      running: complete
        ? containers.data.filter((c) => RUNNING_STATUSES.includes(c.status)).length
        : undefined,
    };
  }, [containers.loading, containers.error, containers.data, containers.total]);
  const count = (h: { loading: boolean; error: string | null; total: number }) =>
    !h.loading && h.error === null ? h.total : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Fleet health at a glance."
        actions={<UpdatedLabel updatedAt={latest} onRefresh={refetchAll} />}
      />

      <AttentionStrip items={attention} incomplete={incomplete} />

      <StatsRow
        instances={instances}
        endpoints={endpoints}
        apps={apps}
        containers={containerStats}
        deployments24h={deployments24h}
        capacityPercent={capacityPercent}
      />

      <Panel
        title="Capacity by endpoint"
        loading={endpointsP.loading || appsP.loading}
        error={endpointsP.error ?? appsP.error}
        hasData={endpoints !== undefined && apps !== undefined}
        onRetry={() => {
          refetchEndpoints();
          refetchApps();
        }}
      >
        <EndpointCapacityPanel items={capacityItems} />
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel
          title="Recent deployments"
          loading={deploymentsP.loading}
          error={deploymentsP.error}
          hasData={deployments !== undefined}
          onRetry={refetchDeployments}
        >
          <RecentDeploymentsPanel deployments={deployments ?? []} endpointNames={endpointNames} />
        </Panel>
        <Panel
          title="Recent catalogs"
          loading={catalogsP.loading}
          error={catalogsP.error}
          hasData={catalogsP.data !== undefined}
          onRetry={refetchCatalogs}
        >
          <RecentCatalogsPanel catalogs={catalogsP.data ?? []} deployedCounts={deployedCounts} />
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel
          title="Recent activity"
          className="lg:col-span-2"
          loading={logsP.loading}
          error={logsP.error}
          hasData={logsP.data !== undefined}
          onRetry={refetchLogs}
        >
          <ActivityPanel logs={logsP.data ?? []} />
        </Panel>
        <Panel
          title="Infrastructure"
          loading={infraLoading && !infraResolved}
          error={infraError ?? (infraPartialError ? 'Some counts failed to load.' : null)}
          hasData={infraResolved}
          onRetry={refetchInfra}
        >
          <InfrastructurePanel
            counts={{
              containers: containerStats,
              volumes: count(volumes),
              networks: count(networks),
              stacks: count(stacks),
              services: count(services),
            }}
          />
        </Panel>
      </div>
    </div>
  );
}
